import React, { useEffect, useState, Suspense } from 'react';
import { Navigate, Route, Routes, useMatch, useNavigate, useLocation } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { useOktaAuth } from '@okta/okta-react';
import moment from 'moment';
import 'focus-visible';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { toRelativeUrl } from '@okta/okta-auth-js';

import { searchSplitter } from 'helpers/urlHelpers';

import { GET_CURRENT_USER, GET_CARRIER_REP } from 'api/queries';
import {
  UserContext,
  LoadDetailStatusContext,
  CurrentUserDefault
} from 'components/Providers/Providers';
import type { CurrentUserType } from 'sharedTypes/user/userTypes';
import MediaQueryProvider, { mediaQueries, MediaQueryContext } from '@arrive/responsive-provider';
import { LoadSignalrProvider } from 'components/Providers/LoadSignalrProvider';
import { ActiveTabProvider } from 'components/Providers/ActiveTabProvider';
import ToastProvider from '@arrive/toasts';
import { OfferStatusProvider } from 'components/Providers/OfferStatusProvider';
import FilterTrackingProvider from './Providers/FilterTrackingProvider';
import { BookItNowModalOpenProvider } from './Providers/BookItNowProviders';

import NavigationBar from 'components/Navigation/Navigation';
import Footer from 'components/Footer/Footer';
import { ErrorWrapper } from 'components/ErrorWrapper/ErrorWrapper';
import NoResults from 'components/NoResults/NoResults';
import CustomLoginCallback from 'components/OktaErrorHandler/CustomLoginCallback';
import CommittedCapacity from 'components/CommittedCapacity/CommittedCapacity';
import CPCommittedCapacity from 'components/CPCommittedCapacity/CPCommittedCapacity';
import SecureRoute from 'components/SecureRoute/SecureRoute';

import { showDedicatedOpportunities, showCommittedCapacity } from 'utils/permissionUtils';
import CodedError from 'components/ErrorScreens/CodedError';

import { useSaveUserLastLogin } from 'hooks/useSaveUserLastLogin';
import useLastLocation from 'hooks/useLastLocation';
import { useDATBookingRedirect } from 'hooks/useDATBookingRedirect';

import { ROUTES } from 'constants/routeConstants';
import NO_RESULTS from 'assets/no-results.svg';

import AppStyle from './App.scss';

const NotFoundPage = () => {
  return (
    <div data-testid='not-found-page'>
      <NoResults
        icon={NO_RESULTS}
        title='404 Not Found'
      />
    </div>
  )
}

const getHashes = function (hash: string) {
  const params = {} as { [key: string]: string };
  (hash || '').replace(/[(?&|#)]+([^=]+)=([^&]*)/gi, function (_: string, key: string, val: string) {
    params[key] = val
    return val;
  });

  return params;
}

const LandingPage = React.lazy(() => import(/* webpackChunkName: "LandingPages" */ 'components/LandingPage/LandingPage'));
const LoadDetail = React.lazy(() => import(/* webpackChunkName: "LoadDetails" */ 'components/LoadDetail/LoadDetail'));
const MyLoads = React.lazy(() => import(/* webpackChunkName: "LoadBoards" */ 'components/MyLoads/MyLoads'));
const LoadBoard = React.lazy(() => import(/* webpackChunkName: "LoadBoards" */ 'components/LoadBoard/LoadBoard'));
const MyOffers = React.lazy(() => import(/* webpackChunkName: "LoadBoards" */ 'components/MyOffers/MyOffers'));
const DedicatedOpportunities = React.lazy(() => import(/* webpackChunkName: "LoadBoards" */ '../pages/LaneOpportunities/LaneOpportunities'));
// bundle all the account screens together
const Profile = React.lazy(() => import(/* webpackChunkName: "Account" */ 'components/Profile/Profile'));
const MyCompany = React.lazy(() => import(/* webpackChunkName: "Account" */ 'components/MyCompany/MyCompany'));
const ContactUs = React.lazy(() => import(/* webpackChunkName: "Account" */ 'components/ContactUs/ContactUs'));
const SideNavigation = React.lazy(() => import(/* webpackChunkName: "Account" */ 'components/SideNavigation/SideNavigation'));
const ContactRepBoxMyCompany = React.lazy(() => import(/* webpackChunkName: "Account" */ 'components/MyCompany/ContactRepBox/ContactRepBox'));
const SuggestedLoads = React.lazy(() => import(/* webpackChunkName: "Account" */ 'components/SuggestedLoads/SuggestedLoads'));


const hasCarrierId = (currentUser: CurrentUserType, loading: boolean) => {
  if (!loading && currentUser.OktaId) {
    return !!currentUser.CarrierId;
  }
  // default true if user hasn't loaded yet
  return true;
}

const WrappedLoadBoard = (props: any) => {

  return <FilterTrackingProvider><LoadSignalrProvider><LoadBoard {...props} /></LoadSignalrProvider></FilterTrackingProvider>
}

const clearAllUserSessionData = () => {
  localStorage.removeItem('okta-token-storage');
  localStorage.removeItem('okta-cache-storage');
}

const LoadDetailWrapper = ({setLoadDetailLoadAvailable}: {setLoadDetailLoadAvailable: (loadAvail: boolean) => void}) => {
  return <LoadDetail setLoadDetailLoadAvailable={setLoadDetailLoadAvailable} />;
};

const ProfileWrapper = ( {setUser}: {setUser: (newUser: CurrentUserType) => void}) => {
  return <Profile setUser={setUser} />
}

export default function App() {
  const { authState } = useOktaAuth();
  const [LDloading, setLDLoading] = useState(true)
  const [loadDetailLoadAvailable, setLoadDetailLoadAvailable] = useState(true);
  const [user, setUser] = useState(CurrentUserDefault);
  const [getUser, { data = { getCurrentUser: CurrentUserDefault, getCarrierInfo: {}, getIsCommittedCarrier: {} }, loading, error: userFetchError }] = useLazyQuery(GET_CURRENT_USER);
  const [getCarrierRep, { data: carrierRep = { getCurrentUser: null }, loading: carrierRepLoading }] = useLazyQuery(GET_CARRIER_REP);
  const implicitCallbackRoute = useMatch(ROUTES.authCallback);
  const userProfileRoute = useMatch(ROUTES.profile);
  const navigate = useNavigate();
  const location = useLocation();
  const ldClient = useLDClient();
  const { FEATURE_COMMITTED_CAPACITY, FEATURE_MATCHES_STANDALONE, FEATURE_LANE_OPPORTUNITIES, BYPASS_UNIT_COUNT_CHECK } = useFlags();
  const lastLocation = useLastLocation();
  const showCommitmentsTab = data.getIsCommittedCarrier.Committed;
  const { saveLastLogin } = useSaveUserLastLogin();

  const { getDATRedirectQueryParam, handleDatBookingRedirect } = useDATBookingRedirect();

  function sendDATRedirectPendoEvent(isAuthenticated: boolean) {
    const originalUrl = toRelativeUrl(location.pathname, window.location.origin);
    const fullUrl = `${originalUrl}${location.search || ''}`
    const datRedirectQueryParam = getDATRedirectQueryParam(fullUrl);
    handleDatBookingRedirect(fullUrl, datRedirectQueryParam, isAuthenticated)
  }

  useEffect(() => {
    if (authState?.accessToken) {
      const issuerUrl = authState?.accessToken.authorizeUrl.replace('/v1/authorize', '')
      if (issuerUrl && issuerUrl !== process.env.OKTA_ISSUER) {
        clearAllUserSessionData();
      }
    }
  }, [authState])

  useEffect(() => {
    if (authState?.isAuthenticated) {
      getUser();
      saveLastLogin();
    }
  }, [authState, getCarrierRep, getUser, saveLastLogin]);

  useEffect(() => {
    if (!loading && data.getCurrentUser !== CurrentUserDefault) {
      setUser({ ...data.getCurrentUser, ...data.getCarrierInfo, ...data.getIsCommittedCarrier })
      getCarrierRep();
    }
  }, [data, getCarrierRep, loading])

  useEffect(() => {
    if (!carrierRepLoading && carrierRep.getCurrentUser) {
      setUser(curUser => ({ ...curUser, ...carrierRep.getCurrentUser }))
    }
  }, [carrierRepLoading, carrierRep])

  useEffect(() => {
    if (authState?.isAuthenticated && !implicitCallbackRoute && userProfileRoute) {
      const oktaToken = localStorage.getItem('okta-api-storage');
      let tokenExpired = true;
      if (oktaToken) {
        const parsedToken = JSON.parse(oktaToken);
        tokenExpired = moment(parsedToken.expires).isSameOrBefore();
      }
      if (tokenExpired) {
        const oktaUrl = `${process.env.OKTA_BASE}/oauth2/v1/authorize?prompt=none&client_id=${process.env.OKTA_CLIENT_ID}&response_type=token&response_mode=fragment&scope=okta.users.manage.self&redirect_uri=${process.env.OKTA_REDIRECT_URI}&nonce=UBGW&state=1234`;
        // eslint-disable-next-line
        // @ts-ignore window.location does allow this
        window.location = oktaUrl
      }
    }
  }, [authState, navigate, implicitCallbackRoute, userProfileRoute])

  useEffect(() => {
    if (authState?.isAuthenticated !== undefined && window.pendo) {
      if (!authState.isAuthenticated) {
        sendDATRedirectPendoEvent(authState.isAuthenticated);
      }

      if (authState.isAuthenticated && user.UserId && user.CarrierRep !== undefined) {
        window.pendo.initialize({
          visitor: {
            id: user.OktaId,
            xlr8_id: user.UserId,
            email: user.EmailAddress,
            full_name: user.FullName,
            first_name: user.FirstName,
            last_name: user.LastName,
            carrier_id: user.CarrierId !== -1 ? user.CarrierId : null,
            carrier_rep_name: user.CarrierRep?.FullName || 'Unassigned',
            carrier_rep_direct_manager: user.CarrierRep?.DirectManagerUserFullName,
            unit_count: user.UnitCount
          },
          account: {
            id: user.CarrierId ? `External Carrier: ${user.CarrierId}` : 'Internal Employee',
            UserType: user.CarrierId ? 'Carrier' : 'Internal',
            AccountName: user.CarrierName,
            CarrierCode: user.CarrierCode
          }
        });
        sendDATRedirectPendoEvent(authState.isAuthenticated)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState, user])

  useEffect(() => {
    if (user !== CurrentUserDefault) {
      setLDLoading(true)
      ldClient.identify({
        kind: 'user',
        key: `${user.UserId}`,
        firstName: user.FirstName,
        lastName: user.LastName,
        name: user.FullName,
        email: user.EmailAddress,
        custom: {
          carrierId: user.CarrierId,
          carrierCode: user.CarrierCode
        }
      }).finally(() => {
        setLDLoading(false)
      })
    }

  }, [ldClient, user])

  useEffect(() => {
    if (implicitCallbackRoute && window.location.hash) {
      const hashes = getHashes(location.hash)
      if (hashes.access_token) {
        localStorage.setItem('okta-api-storage', JSON.stringify({
          token: hashes.access_token,
          expires: moment().add(hashes.expires_in, 's').valueOf()
        }))
        navigate(ROUTES.profile)
      }
    }
    // eslint-disable-next-line
  }, [navigate, implicitCallbackRoute]);

  const UserInfoSectionPaths = [ROUTES.profile, ROUTES.contactUs];

  const userHasCarrierId = hasCarrierId(user, loading);


  useEffect(() => {
    const carrierOnlyRoutes = [ROUTES.myLoads, ROUTES.myCompany, ROUTES.myOffers, ROUTES.dedicatedOpportunities];
    if (userFetchError) {
      if (carrierOnlyRoutes.includes(location.pathname) && userFetchError.message.includes('User does not have an associated Carrier')) {
        // redirect to not-found page
        navigate('/not-found')
      } else {
        throw userFetchError;
      }
    }
  });

  useEffect(() => {
    if (location && lastLocation) {
      const { search: lastSearch } = lastLocation;
      const { search, pathname } = location;

      const lastSearchParams = searchSplitter(lastSearch);
      if (lastSearchParams?.env && !searchSplitter(search)?.env) {
        const currentLocationHasParams = !!search?.split('?')[1];
        navigate(`${pathname}${search}${currentLocationHasParams ? '&' : '?'}env=${lastSearchParams.env}`, {replace: true})
      }
    }
  }, [lastLocation, location, navigate]);

  if (userHasCarrierId) {
    UserInfoSectionPaths.push(ROUTES.myCompany);
  }

  return (
    <UserContext.Provider value={user}>
      <LoadDetailStatusContext.Provider value={loadDetailLoadAvailable}>
        <MediaQueryProvider queries={mediaQueries}>
          <ActiveTabProvider>
            <ToastProvider>
              <OfferStatusProvider>
                <NavigationBar />
                <Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <ErrorWrapper className={AppStyle.viewport} location={location} user={user}>
                    <MediaQueryContext.Consumer>
                      {(mediaQueryValues) => (
                        <Routes>
                          {/* Public Routes */}
                          <Route path={ROUTES.authCallback} element={<CustomLoginCallback />} />
                          <Route path={ROUTES.notFound} element={<NotFoundPage/>} />
                          <Route path={ROUTES.codedError} element={<CodedError/>} />
                          <Route path='*' element={<Navigate to={ROUTES.notFound} replace/>} />
                          {/* Secure Routes */}
                          <Route path='/' element={
                            <SecureRoute>
                              <LoadSignalrProvider>
                                <BookItNowModalOpenProvider>
                                  <LandingPage />
                                </BookItNowModalOpenProvider>
                              </LoadSignalrProvider>
                            </SecureRoute>
                          } />
                          <Route path={ROUTES.loadBoard} element={<SecureRoute><WrappedLoadBoard/></SecureRoute>} />
                          {userHasCarrierId && <Route path={ROUTES.myLoads} element={<SecureRoute><MyLoads/></SecureRoute>} />}
                          {userHasCarrierId && <Route path={ROUTES.myOffers} element={<SecureRoute><MyOffers/></SecureRoute>} />}
                          <Route
                            path={ROUTES.loadDetail}
                            element={
                              <SecureRoute>
                                <LoadSignalrProvider>
                                  <BookItNowModalOpenProvider>
                                    <LoadDetailWrapper setLoadDetailLoadAvailable={setLoadDetailLoadAvailable}/>
                                  </BookItNowModalOpenProvider>
                                </LoadSignalrProvider>
                              </SecureRoute>
                            }
                          />
                          {UserInfoSectionPaths.map(path => {
                            return (
                              <Route path={path} key={path} element={
                                <SecureRoute>
                                  <SideNavigation>
                                    {path === ROUTES.profile && <ProfileWrapper setUser={setUser}/>}
                                    {path === ROUTES.myCompany && userHasCarrierId && <MyCompany/>}
                                    {path === ROUTES.contactUs && <ContactUs/>}
                                    {(!(mediaQueryValues.sm || mediaQueryValues.xs)) && (path === ROUTES.myCompany || path === ROUTES.profile) ? <ContactRepBoxMyCompany/> : null}
                                  </SideNavigation>
                                </SecureRoute>
                              }/>
                            )
                          })}
                          <Route path='/' element={
                            <SecureRoute>
                              <LoadSignalrProvider>
                                <BookItNowModalOpenProvider>
                                  <LandingPage />
                                </BookItNowModalOpenProvider>
                              </LoadSignalrProvider>
                            </SecureRoute>
                          } />
                          {FEATURE_COMMITTED_CAPACITY && <Route path={ROUTES.processOffer} element={<SecureRoute><CommittedCapacity/></SecureRoute>} />}
                          {FEATURE_MATCHES_STANDALONE && <Route path={ROUTES.suggestedLoads} element={<SecureRoute><SuggestedLoads/></SecureRoute>} /> }
                          {showCommittedCapacity(user, loading, showCommitmentsTab) && <Route path={ROUTES.committedCapacity} element={<SecureRoute><CPCommittedCapacity/></SecureRoute>} />}
                          {showDedicatedOpportunities(user, (loading || LDloading), FEATURE_LANE_OPPORTUNITIES, BYPASS_UNIT_COUNT_CHECK) && <Route path={ROUTES.dedicatedOpportunities} element={<SecureRoute><DedicatedOpportunities/></SecureRoute>} />}
                        </Routes>
                      )}
                    </MediaQueryContext.Consumer>
                  </ErrorWrapper>
                </Suspense>
                <Footer />
              </OfferStatusProvider>
            </ToastProvider>
          </ActiveTabProvider>
        </MediaQueryProvider>
      </LoadDetailStatusContext.Provider>
    </UserContext.Provider>

  )
}
